if (!customElements.get('purchase-options')) {
  class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      if (this.sync) return;

      this.formSelector = `#${this.dataset.formId}, #${this.dataset.stickyFormId}`;

      // Both price blocks show the selected option, the sticky cart mirrors the product.
      this.sync = new window.PdpSelectionSync({
        formSelector: this.formSelector,
        priceSelector: `#${this.dataset.priceId}, #${this.dataset.stickyPriceId}`,
        subscribeLabel: this.dataset.subscribeLabel,
        removeLabel: this.dataset.removeLabel
      });

      // Rendered with the cart, so the button is already right on the first paint.
      this.setSubscriptionKeys((this.dataset.subscriptionKeys || '').split(',').filter(Boolean));

      this.modes = this.querySelector('.purchase-options__modes');
      this.modeButtons = this.querySelectorAll('.purchase-options__mode');
      this.modeBadge = this.querySelector('[data-mode-badge]');

      this.modeButtons.forEach((button) => {
        button.addEventListener('click', () => this.setMode(button.dataset.mode));
      });

      this.addEventListener('change', (event) => {
        if (event.target.matches('.purchase-option__input')) {
          this.select(event.target);
        }
      });

      // The submit button of every synced form removes instead of adding once flagged.
      document.addEventListener('click', (event) => this.onClick(event));

      subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event.data));
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshFromCart());
      document.addEventListener('cart:updated', () => this.refreshFromCart());

      this.setMode(this.mode);
    }

    get group() {
      return this.querySelector('.purchase-options__variant:not([hidden])');
    }

    get mode() {
      const selected = this.querySelector('.purchase-options__mode--selected');
      return selected ? selected.dataset.mode : 'onetime';
    }

    setMode(mode) {
      const group = this.group;
      if (!group) return;

      // A variant without selling plans can only be bought once, it needs no tabs.
      const withoutSubscription = group.dataset.subscriptionCount === '0';
      if (mode === 'subscription' && withoutSubscription) {
        mode = 'onetime';
      }

      if (this.modes) {
        this.modes.hidden = withoutSubscription;
      }

      this.modeButtons.forEach((button) => {
        const isSelected = button.dataset.mode === mode;
        button.classList.toggle('purchase-options__mode--selected', isSelected);
        button.setAttribute('aria-pressed', isSelected);
      });

      this.querySelectorAll('.purchase-options__list').forEach((list) => {
        list.hidden = list.dataset.mode !== mode;
      });

      const list = group.querySelector(`.purchase-options__list[data-mode="${mode}"]`);
      const input = list
        ? list.querySelector('.purchase-option__input:checked') || list.querySelector('.purchase-option__input')
        : null;

      if (input) {
        input.checked = true;
        this.select(input);
      }
    }

    select(input) {
      this.selected = input;

      this.querySelectorAll('.purchase-option').forEach((option) => {
        option.classList.toggle('purchase-option--selected', option.contains(input));
      });

      this.classList.toggle('purchase-options--subscription', Boolean(input.dataset.sellingPlan));

      this.sync.update({
        quantity: input.dataset.quantity,
        sellingPlanId: input.dataset.sellingPlan,
        priceMoney: input.dataset.priceMoney,
        compareMoney: input.dataset.compareMoney,
        unitMoney: input.dataset.unitMoney,
        label: input.dataset.label
      });
    }

    /*
      Every subscription line of this product, whatever plan or variant it was added
      with: one subscription per product is the most the widget offers.
    */
    setSubscriptionKeys(keys) {
      this.subscriptionKeys = keys;
      this.sync.removeMode = keys.length > 0;

      if (this.selected) {
        this.select(this.selected);
      }
    }

    refreshFromCart() {
      const productId = Number(this.dataset.productId);

      return fetch(`${theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const keys = cart.items
            .filter((item) => item.selling_plan_allocation && item.product_id === productId)
            .map((item) => item.key);
          this.setSubscriptionKeys(keys);
        })
        .catch(() => {
          // Without an answer the button keeps whatever it shows right now.
        });
    }

    onClick(event) {
      const button = event.target.closest('[data-remove-subscription="true"]');
      if (!button || !button.form || !button.form.matches(this.formSelector)) return;

      event.preventDefault();
      this.removeSubscription(button);
    }

    removeSubscription(button) {
      if (!this.subscriptionKeys.length || button.classList.contains('loading')) return;

      button.classList.add('loading');
      button.setAttribute('aria-disabled', true);

      const miniCart = document.querySelector('mini-cart');
      const sections = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];

      // One request per line, the keys stay valid while the other lines move.
      const removals = this.subscriptionKeys.reduce(
        (chain, key) =>
          chain.then(() =>
            fetch(`${theme.routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify({
                id: key,
                quantity: 0,
                sections: sections,
                sections_url: window.location.pathname
              })
            }).then((response) => response.json())
          ),
        Promise.resolve(null)
      );

      return removals
        .then((cart) => {
          if (cart && miniCart) {
            this.renderCartSections(miniCart, cart);
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'purchase-options' });
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
        });
    }

    /* Same sections the drawer renders after an add, only without opening it. */
    renderCartSections(miniCart, cart) {
      if (!cart.sections) return;

      miniCart.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        const html = cart.sections[section.id];
        if (element && html) {
          element.innerHTML = miniCart.getSectionInnerHTML(html, section.selector);
        }
      });
    }

    onVariantChange({ variant }) {
      if (!variant) return;

      const groups = this.querySelectorAll('.purchase-options__variant');
      groups.forEach((group) => {
        group.hidden = Number(group.dataset.variantId) !== variant.id;
      });

      const group = this.group;
      if (!group) return;

      if (this.modeBadge) {
        const discount = parseInt(group.dataset.maxDiscount, 10) || 0;
        this.modeBadge.textContent = discount ? `-${discount}%` : '';
        this.modeBadge.hidden = !discount;
      }

      this.setMode(this.mode);
    }
  }

  customElements.define('purchase-options', PurchaseOptions);
}
